import React from "react";
import { connect, shallowEqual, useSelector } from "react-redux";
import * as categoryRedux from "modules/Category/_redux/categoryRedux";
import { formatThousandsDot } from "helpers/FormatNumbersWithComma";
import { floorLabel } from "helpers/floorOrder";
import "./sahibindenFilters.css";

const priceLabel = (range) => {
  const [min, max] = range || [];
  if (min && max)
    return `Fiyat: ${formatThousandsDot(min)} - ${formatThousandsDot(max)} TL`;
  if (max) return `Fiyat: ≤ ${formatThousandsDot(max)} TL`;
  if (min) return `Fiyat: ≥ ${formatThousandsDot(min)} TL`;
  return null;
};

const rangeLabel = (title, range) => {
  const [min, max] = range || [];
  const lo = min !== "" && min != null ? min : null;
  const hi = max !== "" && max != null ? max : null;
  if (lo && hi) return `${title}: ${formatThousandsDot(lo)} - ${formatThousandsDot(hi)}`;
  if (hi) return `${title}: ≤ ${formatThousandsDot(hi)}`;
  if (lo) return `${title}: ≥ ${formatThousandsDot(lo)}`;
  return null;
};

// Bulunduğu kat aralığı ordinal index tutar; chip'te etikete çevrilir.
const floorRangeLabel = (range) => {
  const [min, max] = range || [];
  const loIdx = min !== "" && min != null ? Number(min) : null;
  const hiIdx = max !== "" && max != null ? Number(max) : null;
  const lo = loIdx != null ? floorLabel(loIdx) : null;
  const hi = hiIdx != null ? floorLabel(hiIdx) : null;
  if (lo && hi) return `Bulunduğu Kat: ${lo} - ${hi}`;
  if (lo) return `Bulunduğu Kat: ${lo} ve üzeri`;
  if (hi) return `Bulunduğu Kat: ${hi} ve altı`;
  return null;
};

function ActiveFilters(props) {
  const { selectedFilter, filters, filterLabels } = useSelector(
    (state) => ({
      selectedFilter: state.category.selectedFilter,
      filters: state.category.categoryDetail.filters,
      filterLabels: state.category.filterLabels,
    }),
    shallowEqual
  );

  const filterTitle = (slug) =>
    (filters || []).find((f) => f.slug === slug)?.title;

  const chips = [];

  Object.entries(selectedFilter || {}).forEach(([key, value]) => {
    if (key === "kind") {
      const kind = Array.isArray(value) ? value[0] : value;
      if (!kind) return;
      chips.push({
        id: "kind",
        label: kind === "seller" ? "Satılan" : "Alınan",
        onRemove: () => props.removeFilter({ parent: "kind" }),
      });
    } else if (key === "listingType") {
      const listingType = Array.isArray(value) ? value[0] : value;
      if (!listingType) return;
      chips.push({
        id: "listingType",
        label: listingType === "rent" ? "Kiralık" : "Satılık",
        onRemove: () => props.removeFilter({ parent: "listingType" }),
      });
    } else if (key === "location") {
      if (value?.province)
        chips.push({
          id: "il",
          label: `İl: ${value.province.name}`,
          onRemove: () => props.removeFilter({ parent: "location" }),
        });
      if (value?.district)
        chips.push({
          id: "ilce",
          label: `İlçe: ${value.district.name}`,
          // İlçeyi kaldır, ili koru: location'ı yalnızca province ile yeniden kur.
          onRemove: () =>
            props.setSelectedProvince({
              id: value.province.id,
              name: value.province.name,
            }),
        });
      if (value?.neighborhood)
        chips.push({
          id: "mahalle",
          label: `Mahalle: ${value.neighborhood}`,
          onRemove: () => props.setSelectedNeighborhood(null),
        });
    } else if (key === "minMaxFiyat") {
      const label = priceLabel(value);
      if (label)
        chips.push({
          id: "fiyat",
          label,
          onRemove: () => props.removeFilter({ parent: "minMaxFiyat" }),
        });
    } else if (key === "minMaxKm") {
      const label = rangeLabel("KM", value);
      if (label)
        chips.push({
          id: "km",
          label,
          onRemove: () => props.removeFilter({ parent: "minMaxKm" }),
        });
    } else if (key === "minMaxYear") {
      const label = rangeLabel("Yıl", value);
      if (label)
        chips.push({
          id: "yil",
          label,
          onRemove: () => props.removeFilter({ parent: "minMaxYear" }),
        });
    } else if (key === "minMaxM2") {
      const label = rangeLabel("m²", value);
      if (label)
        chips.push({
          id: "m2",
          label,
          onRemove: () => props.removeFilter({ parent: "minMaxM2" }),
        });
    } else if (key === "minMaxKatSayisi") {
      const label = rangeLabel("Kat Sayısı", value);
      if (label)
        chips.push({
          id: "katSayisi",
          label,
          onRemove: () => props.removeFilter({ parent: "minMaxKatSayisi" }),
        });
    } else if (key === "minMaxBulunduguKat") {
      const label = floorRangeLabel(value);
      if (label)
        chips.push({
          id: "bulunduguKat",
          label,
          onRemove: () => props.removeFilter({ parent: "minMaxBulunduguKat" }),
        });
    } else if (key === "vehicleBrand") {
      chips.push({
        id: "marka",
        label: (filterLabels || {}).vehicleBrand
          ? `Marka: ${filterLabels.vehicleBrand}`
          : "Marka",
        onRemove: () => {
          props.removeFilter({ parent: "vehicleBrand" });
          props.removeFilter({ parent: "vehicleModel" });
          props.removeFilterLabel("vehicleBrand");
          props.removeFilterLabel("vehicleModel");
        },
      });
    } else if (key === "vehicleModel") {
      chips.push({
        id: "model",
        label: (filterLabels || {}).vehicleModel
          ? `Model: ${filterLabels.vehicleModel}`
          : "Model",
        onRemove: () => {
          props.removeFilter({ parent: "vehicleModel" });
          props.removeFilterLabel("vehicleModel");
        },
      });
    } else if (Array.isArray(value)) {
      const title = filterTitle(key);
      value.forEach((v) =>
        chips.push({
          id: `${key}:${v}`,
          label: title ? `${title}: ${v}` : String(v),
          onRemove: () => props.removeFilter({ parent: key, value: v }),
        })
      );
    }
  });

  if (chips.length === 0) return null;

  return (
    <div className="af-bar">
      <span className="af-bar-title">Uygulanan kriterler:</span>
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          className="af-chip"
          onClick={chip.onRemove}
        >
          <span>{chip.label}</span>
          <span className="af-chip-x">&times;</span>
        </button>
      ))}
      <button
        type="button"
        className="af-clear-all"
        onClick={() => props.resetFilter()}
      >
        Tümünü temizle
      </button>
    </div>
  );
}

export default connect(null, { ...categoryRedux.actions })(ActiveFilters);
